import { StandaloneServices } from 'monaco-editor/esm/vs/editor/standalone/browser/standaloneServices.js';
import { positionFromString, positionToString } from 'vscode/vscode/vs/workbench/services/layout/browser/layoutService';
import { ILayoutService } from 'monaco-editor/esm/vs/platform/layout/browser/layoutService.js';
import { Emitter, Event } from 'monaco-editor/esm/vs/base/common/event.js';
import * as dom from 'monaco-editor/esm/vs/base/browser/dom.js';
import { isAncestorUsingFlowTo } from 'monaco-editor/esm/vs/base/browser/dom.js';
import { SyncDescriptor } from 'monaco-editor/esm/vs/platform/instantiation/common/descriptors.js';
import { IPaneCompositePartService } from 'vscode/vscode/vs/workbench/services/panecomposite/browser/panecomposite';
import { IViewDescriptorService } from 'vscode/vscode/vs/workbench/common/views';
import { isChrome, isFirefox, isSafari, isWindows, isLinux } from 'monaco-editor/esm/vs/base/common/platform.js';
import { coalesce } from 'monaco-editor/esm/vs/base/common/arrays.js';
import { IEditorGroupsService } from 'vscode/vscode/vs/workbench/services/editor/common/editorGroupsService';
import { IStatusbarService } from 'vscode/vscode/vs/workbench/services/statusbar/browser/statusbar';
import { IConfigurationService } from 'monaco-editor/esm/vs/platform/configuration/common/configuration.js';
import { Disposable, DisposableStore, toDisposable } from 'monaco-editor/esm/vs/base/common/lifecycle.js';
import { IAuxiliaryWindowService } from 'vscode/vscode/vs/workbench/services/auxiliaryWindow/browser/auxiliaryWindowService';
import { StandaloneCodeEditor } from 'monaco-editor/esm/vs/editor/standalone/browser/standaloneCodeEditor.js';
import { IHostService } from 'vscode/vscode/vs/workbench/services/host/browser/host';
import { ICodeEditorService } from 'monaco-editor/esm/vs/editor/browser/services/codeEditorService.js';
import { getMenuBarVisibility, getTitleBarStyle } from 'vscode/vscode/vs/platform/window/common/window';
import { onRenderWorkbench } from 'vscode/lifecycle';
import { getWorkbenchContainer } from 'vscode/workbench';

class LayoutService extends Disposable {
    constructor(mainContainer = getWorkbenchContainer()) {
        super();
        this.mainContainer = mainContainer;
        this._onDidLayoutContainer = this._register(new Emitter());
        this.onDidLayoutContainer = this._onDidLayoutContainer.event;
        this._onDidAddContainer = this._register(new Emitter());
        this.onDidAddContainer = this._onDidAddContainer.event;
        this._onDidRemoveContainer = this._register(new Emitter());
        this.onDidRemoveContainer = this._onDidRemoveContainer.event;
        this._onDidLayoutMainContainer = this._register(new Emitter());
        this.onDidLayoutMainContainer = this._onDidLayoutMainContainer.event;
        this._onDidLayoutActiveContainer = this._register(new Emitter());
        this.onDidLayoutActiveContainer = this._onDidLayoutActiveContainer.event;
        this._onDidChangeActiveContainer = this._register(new Emitter());
        this.onDidChangeActiveContainer = this._onDidChangeActiveContainer.event;
        this.mainContainerOffset = { top: 0, quickPickTop: 0 };
        this.activeContainerOffset = { top: 0, quickPickTop: 0 };
        this.onDidChangeFullscreen = Event.None;
        this.onDidChangeZenMode = Event.None;
        this.onDidChangeWindowMaximized = Event.None;
        this.onDidChangeCenteredLayout = Event.None;
        this._onDidChangePanelPosition = this._register(new Emitter());
        this.onDidChangePanelPosition = this._onDidChangePanelPosition.event;
        this._onDidChangeSideBarPosition = this._register(new Emitter());
        this.onDidChangeSideBarPosition = this._onDidChangeSideBarPosition.event;
        this.onDidChangePanelAlignment = Event.None;
        this.onDidChangeNotificationsVisibility = Event.None;
        this.openedDefaultEditors = false;
        this.whenRestored = Promise.resolve();
        this.parts = new Map();
        this.hiddenParts = new Set();
        this._onDidChangePartVisibility = new Emitter();
        this.onDidChangePartVisibility = this._onDidChangePartVisibility.event;
        this.offset = { top: 0, quickPickTop: 0 };
        this._onDidLayout = new Emitter();
        this.onDidLayout = this._onDidLayout.event;
        window.addEventListener('resize', () => this.layout());
        this.layout();
    }
    hasMainWindowBorder() {
        return false;
    }
    getMainWindowBorderRadius() {
        return undefined;
    }
    isMainEditorLayoutCentered() {
        return false;
    }
    centerMainEditorLayout() {
    }
    get activeContainer() { return this.getContainerFromDocument(dom.getActiveDocument()); }
    get containers() {
        const containers = [];
        for (const { window } of dom.getWindows()) {
            containers.push(this.getContainerFromDocument(window.document));
        }
        return containers;
    }
    getContainerFromDocument(document) {
        if (document === this.mainContainer.ownerDocument) {
            return this.mainContainer;
        }
        else {
            return document.body.getElementsByClassName('monaco-workbench')[0];
        }
    }
    init(accessor) {
        this.editorGroupService = accessor.get(IEditorGroupsService);
        this.paneCompositeService = accessor.get(IPaneCompositePartService);
        this.statusBarService = accessor.get(IStatusbarService);
        this.viewDescriptorService = accessor.get(IViewDescriptorService);
        this.configurationService = accessor.get(IConfigurationService);
        this.auxiliaryWindowService = accessor.get(IAuxiliaryWindowService);
        this.hostService = accessor.get(IHostService);
        this._register(this.configurationService.onDidChangeConfiguration((e) => {
            if (e.affectsConfiguration("workbench.activityBar.location" )) {
                this.setPartHidden(this.isActivityBarHidden(), "workbench.parts.activitybar" );
            }
            if (e.affectsConfiguration('workbench.statusBar.visible')) {
                this.setPartHidden(!this.configurationService.getValue('workbench.statusBar.visible'), "workbench.parts.statusbar" );
            }
            if (e.affectsConfiguration('workbench.sideBar.location')) {
                this.setSideBarPosition(positionFromString(this.configurationService.getValue('workbench.sideBar.location') ?? 'left'));
            }
            if (e.affectsConfiguration('workbench.panel.defaultLocation')) {
                this.setPanelPosition(positionFromString(this.configurationService.getValue('workbench.panel.defaultLocation') ?? 'bottom'));
            }
        }));
        this.setPartHidden(this.isActivityBarHidden(), "workbench.parts.activitybar" );
        this.setPartHidden(!this.configurationService.getValue('workbench.statusBar.visible'), "workbench.parts.statusbar" );
        this.sideBarPosition = positionFromString(this.configurationService.getValue('workbench.sideBar.location') ?? 'left');
        this.panelPosition = positionFromString(this.configurationService.getValue('workbench.panel.defaultLocation') ?? 'bottom');
        this._register(this.hostService.onDidChangeActiveWindow(() => this.onActiveWindowChanged()));
        this._register(this.auxiliaryWindowService.onDidOpenAuxiliaryWindow(({ window, disposables }) => {
            this._onDidAddContainer.fire({ container: window.container, disposables: new DisposableStore() });
            disposables.add(window.onDidLayout(dimension => this.handleContainerDidLayout(window.container, dimension)));
            disposables.add(toDisposable(() => this._onDidRemoveContainer.fire(window.container)));
        }));
    }
    handleContainerDidLayout(container, dimension) {
        if (container === this.mainContainer) {
            this._onDidLayoutMainContainer.fire(dimension);
        }
        if (dom.isActiveDocument(container)) {
            this._onDidLayoutActiveContainer.fire(dimension);
        }
    }
    getActiveContainerId() {
        const activeContainer = this.activeContainer;
        return dom.getWindow(activeContainer).vscodeWindowId;
    }
    onActiveWindowChanged() {
        const activeContainerId = this.getActiveContainerId();
        if (this.activeContainerId !== activeContainerId) {
            this.activeContainerId = activeContainerId;
            this._onDidChangeActiveContainer.fire();
        }
    }
    isActivityBarHidden() {
        const oldValue = this.configurationService.getValue('workbench.activityBar.visible');
        if (oldValue !== undefined) {
            return !oldValue;
        }
        return this.configurationService.getValue("workbench.activityBar.location" ) !== "side" ;
    }
    focusPart(part) {
        switch (part) {
            case "workbench.parts.editor" :
                this.editorGroupService.activeGroup.focus();
                break;
            case "workbench.parts.panel" : {
                const activePanel = this.paneCompositeService.getActivePaneComposite(1 );
                activePanel?.focus();
                break;
            }
            case "workbench.parts.sidebar" : {
                const activeViewlet = this.paneCompositeService.getActivePaneComposite(0 );
                activeViewlet?.focus();
                break;
            }
            case "workbench.parts.activitybar" :
                this.getPart("workbench.parts.activitybar" ).focus();
                break;
            case "workbench.parts.statusbar" :
                this.statusBarService.focus();
                break;
            default: {
                const container = this.getContainer(part);
                container?.focus();
            }
        }
    }
    getDimension(part) {
        return this.getPart(part)?.dimension;
    }
    toggleMaximizedPanel() {
    }
    toggleMenuBar() {
        let currentVisibilityValue = getMenuBarVisibility(this.configurationService);
        if (typeof currentVisibilityValue !== 'string') {
            currentVisibilityValue = 'classic';
        }
        let newVisibilityValue;
        if (currentVisibilityValue === 'visible' || currentVisibilityValue === 'classic') {
            newVisibilityValue = getTitleBarStyle(this.configurationService) === 'native' ? 'toggle' : 'compact';
        }
        else {
            newVisibilityValue = 'classic';
        }
        void this.configurationService.updateValue('window.menuBarVisibility', newVisibilityValue);
    }
    setPanelPosition(position) {
        this.panelPosition = position;
        const panelPart = this.getPart("workbench.parts.panel" );
        panelPart?.updateStyles();
        this._onDidChangePanelPosition.fire(positionToString(position));
    }
    getPanelAlignment() {
        return 'left';
    }
    setPanelAlignment() {
    }
    toggleZenMode() {
    }
    isEditorLayoutCentered() {
        return false;
    }
    centerEditorLayout() {
    }
    resizePart() {
    }
    isWindowMaximized() {
        return false;
    }
    updateWindowMaximizedState() {
    }
    getVisibleNeighborPart() {
        return undefined;
    }
    getMaximumEditorDimensions() {
        return new dom.Dimension(Infinity, Infinity);
    }
    isPanelMaximized() {
        return false;
    }
    getPanelPosition() {
        return this.panelPosition;
    }
    hasFocus(part) {
        const activeElement = document.activeElement;
        if (activeElement == null) {
            return false;
        }
        const container = this.getContainer(part);
        return !(container == null) && isAncestorUsingFlowTo(activeElement, container);
    }
    getContainer(windowOrPart) {
        if (typeof windowOrPart === 'string') {
            if (this.parts.get(windowOrPart) == null) {
                return undefined;
            }
            return this.getPart(windowOrPart)?.getContainer();
        }
        return document.body.getElementsByClassName('monaco-workbench')[0];
    }
    getPart(key) {
        return this.parts.get(key);
    }
    hasViews(id) {
        const viewContainer = this.viewDescriptorService.getViewContainerById(id);
        if (viewContainer == null) {
            return false;
        }
        const viewContainerModel = this.viewDescriptorService.getViewContainerModel(viewContainer);
        if (viewContainerModel == null) {
            return false;
        }
        return viewContainerModel.activeViewDescriptors.length >= 1;
    }
    setPartHidden(hidden, part) {
        if (hidden) {
            this.hiddenParts.add(part);
        }
        else {
            this.hiddenParts.delete(part);
        }
        this._onDidChangePartVisibility.fire();
        const location = {
            ["workbench.parts.sidebar" ]: 0 ,
            ["workbench.parts.auxiliarybar" ]: 2 ,
            ["workbench.parts.panel" ]: 1
        }[part];
        if (location != null) {
            const paneComposite = this.paneCompositeService.getActivePaneComposite(location);
            if (paneComposite != null && hidden) {
                this.paneCompositeService.hideActivePaneComposite(location);
            }
            else if (paneComposite == null && !hidden) {
                let panelToOpen = this.paneCompositeService.getLastActivePaneCompositeId(location);
                if (panelToOpen == null || !this.hasViews(panelToOpen)) {
                    panelToOpen = this.viewDescriptorService
                        .getViewContainersByLocation(location)
                        .find(viewContainer => this.hasViews(viewContainer.id))?.id;
                }
                if (panelToOpen != null) {
                    void this.paneCompositeService.openPaneComposite(panelToOpen, location, true);
                }
            }
        }
        this.getPart(part)?.setVisible(!hidden);
    }
    isVisible(part) {
        return !( this.hiddenParts.has(part));
    }
    getSideBarPosition() {
        return this.sideBarPosition;
    }
    setSideBarPosition(position) {
        this.sideBarPosition = position;
        const activityBar = this.getPart("workbench.parts.activitybar" );
        const sideBar = this.getPart("workbench.parts.sidebar" );
        const auxiliaryBar = this.getPart("workbench.parts.auxiliarybar" );
        activityBar?.updateStyles();
        sideBar?.updateStyles();
        auxiliaryBar?.updateStyles();
        this._onDidChangeSideBarPosition.fire(positionToString(position));
    }
    registerPart(part) {
        this.parts.set(part.getId(), part);
    }
    isRestored() {
        return true;
    }
    get mainContainerDimension() { return this._mainContainerDimension; }
    get activeContainerDimension() {
        const activeContainer = this.activeContainer;
        if (activeContainer === this.mainContainer) {
            return this.mainContainerDimension;
        }
        else {
            return dom.getClientArea(activeContainer);
        }
    }
    layout() {
        this._mainContainerDimension = dom.getClientArea(window.document.body);
        this._onDidLayout.fire(this._mainContainerDimension);
    }
    get hasContainer() {
        return true;
    }
    focus() {
        const activeContainer = this.activeContainer;
        if (activeContainer === this.mainContainer) {
            const focusedCodeEditor = StandaloneServices.get(ICodeEditorService).getFocusedCodeEditor();
            if (focusedCodeEditor instanceof StandaloneCodeEditor) {
                focusedCodeEditor.focus();
            }
            else {
                this.focusPart("workbench.parts.editor" );
            }
        }
        else {
            this.editorGroupService.getPart(activeContainer).activeGroup.focus();
        }
    }
}
onRenderWorkbench((accessor) => {
    const layoutService = accessor.get(ILayoutService);
    if (layoutService instanceof LayoutService) {
        layoutService.init(accessor);
    }
    const platformClass = isWindows ? 'windows' : isLinux ? 'linux' : 'mac';
    const workbenchClasses = coalesce([
        'monaco-workbench',
        platformClass,
        'web',
        isChrome ? 'chromium' : isFirefox ? 'firefox' : isSafari ? 'safari' : undefined
    ]);
    layoutService.mainContainer.classList.add(...workbenchClasses);
    document.body.classList.add(platformClass);
    document.body.classList.add('web');
});
function getServiceOverride(container) {
    return {
        [( ILayoutService.toString())]: new SyncDescriptor(LayoutService, [container], true)
    };
}

export { LayoutService, getServiceOverride as default };
